import "./style.scss";
import { isDefined } from "@clipboard-health/util-ts";
import InfoIcon from "@mui/icons-material/Info";
import { CircularProgress, DialogActions, DialogContent, IconButton, Stack } from "@mui/material";
import { CbhFeatureFlag, useCbhFlags } from "@src/appV2/FeatureFlags";
import { FullScreenDialog, useToast } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { FullScreenDialogTitle } from "@src/appV2/lib/Dialogs/FullScreen/DialogTitle";
import { useCreateNegotiation } from "@src/appV2/Negotiations/api/useCreateNegotiation";
import { useGetNegotiationShiftDetails } from "@src/appV2/Negotiations/api/useGetNegotiationShiftDetails";
import { GET_SHIFT_MODAL_URL } from "@src/appV2/redesign/Shift/Open/useGetShiftModal";
import { useSupportLinks } from "@src/appV2/support/hooks/useSupportLinks";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useZendeskArticle } from "@src/appV2/Zendesk/useZendeskArticle";
import { HCF_PROFILE_SOURCE } from "@src/constants";
import { USER_EVENTS } from "@src/constants/userEvents";
import { formatDollarsAsUsd } from "@src/utils/currency";
import { useQueryClient } from "@tanstack/react-query";
import { findLast } from "lodash";
import moment from "moment-timezone";
import { FC, useEffect, useMemo, useRef, useState } from "react";

import { endNegotiation, fetchNegotiationById, proposeNewRate } from "./api";
import {
  INegotiationToolProps,
  NegotiationCommitType,
  NegotiationHistoryAction,
  NegotiationMetadata,
  NegotiationRateRole,
  NegotiationStatus,
  RateNegotiationHistoryResponse,
  RateNegotiationSchema,
} from "./interfaces";
import { RateNegotiationShiftDetails } from "./NegotiatingShiftsCard";
import { NegotiationHistory } from "./negotiationHistory";
import { NegotiationToolFooter } from "./negotiationToolFooter";
import { RateStepper } from "./rateStepper";

const MIN_NEGOTIATION_LIMIT_IN_MINUTES = 15;

export const NegotiationTool: FC<INegotiationToolProps> = ({
  isOpen,
  closeModal,
  shift,
  showCommitModal,
  closeCommitModal,
  workApproved,
  updateShiftRateNegotiation,
  setRequestLoading,
  isLoadingShiftBook,
  shiftBookability,
  hcfProfileSource,
  onBookShift,
}) => {
  const [pageLoading, setPageLoading] = useState<boolean>(false);
  const [negotiationHistory, setNegotiationHistory] = useState<RateNegotiationHistoryResponse[]>(
    []
  );
  const { openArticle } = useZendeskArticle();
  const { rateNegotiationWorkerSupportLink } = useSupportLinks();

  const ldFlags = useCbhFlags();
  const flagLimits = ldFlags[CbhFeatureFlag.RATE_NEGOTIATION_LIMITS];
  const { userId, alreadyWorked: workerAlreadyWorked, email: workerEmail } = useDefinedWorker();
  const { showErrorToast } = useToast();

  const { mutateAsync: createNegotiation } = useCreateNegotiation();

  const queryClient = useQueryClient();

  const { data: negotiationShiftDetails, isLoading: isGetNegotiationShiftDetailsLoading } =
    useGetNegotiationShiftDetails(
      { shiftId: shift._id ?? "", offerId: shift.offer?.id ?? "" },
      { enabled: isDefined(shift._id) }
    );

  const initialShiftInfo = useMemo(() => {
    return {
      action: NegotiationHistoryAction.INITIAL,
      createdByUserId: shift?.facilityId?.toString() ?? "",
      createdByUserRole: NegotiationRateRole.ADMIN,
      payRate: shift?.rateNegotiation?.initialPayRate || shift?.offer?.pay.value || 0,
      start: shift.start,
      end: shift.end,
    };
  }, [
    shift.end,
    shift?.facilityId,
    shift?.offer?.pay,
    shift?.rateNegotiation?.initialPayRate,
    shift.start,
  ]);

  const { lowerLimit, upperLimit } = useMemo(() => {
    const flagLowerLimit = flagLimits?.Minimum;

    let lastOfferFromHcf = 0;
    let lastOfferByHcp = 0;

    if (
      shift?.rateNegotiation?.metadata.lastOffer.createdByUserRole === NegotiationRateRole.WORKPLACE
    ) {
      lastOfferFromHcf = shift?.rateNegotiation?.metadata?.lastOffer?.payRate ?? 0;
      lastOfferByHcp =
        /**
         * FIXME: This is fragile, since it relies on a physical index offset
         * in the history.
         * This should be based on searching through history for the last HCP offer.
         */
        negotiationHistory[negotiationHistory.length - 2]?.payRate ?? 0;
    } else if (
      shift?.rateNegotiation?.metadata?.lastOffer?.createdByUserRole === NegotiationRateRole.WORKER
    ) {
      lastOfferFromHcf =
        findLast(negotiationHistory, (historyItem: RateNegotiationHistoryResponse) => {
          return historyItem.createdByUserRole === NegotiationRateRole.WORKPLACE;
        })?.payRate ?? 0;

      lastOfferByHcp = shift?.rateNegotiation?.metadata?.lastOffer?.payRate ?? 0;
    }

    const lowerLimit =
      lastOfferFromHcf ||
      initialShiftInfo.payRate ||
      flagLowerLimit ||
      MIN_NEGOTIATION_LIMIT_IN_MINUTES;

    const flagUpperLimit =
      (lastOfferByHcp && lastOfferByHcp - 1) ||
      (shift?.agentReq && flagLimits?.[shift?.agentReq]) ||
      flagLimits?.Maximum ||
      (shift?.offer?.pay.value && 5 * shift?.offer?.pay.value);

    return {
      lowerLimit: Number(lowerLimit),
      upperLimit: Math.min(
        Number(flagUpperLimit),
        negotiationShiftDetails?.data?.attributes?.maxPayRate ?? Number.MAX_SAFE_INTEGER
      ),
    };
  }, [
    flagLimits,
    shift,
    initialShiftInfo.payRate,
    negotiationHistory,
    negotiationShiftDetails?.data?.attributes?.maxPayRate,
  ]);

  const [currentRate, setCurrentRate] = useState<number>(lowerLimit);
  const userHasChangedRate = useRef<boolean>(false);

  // Update currentRate when lowerLimit changes, but only if user hasn't made changes
  useEffect(() => {
    if (!userHasChangedRate.current) {
      setCurrentRate(lowerLimit);
    }
  }, [lowerLimit]);

  useEffect(() => {
    const getHistory = () => {
      setPageLoading(true);

      if (!shift.rateNegotiation?._id) {
        setNegotiationHistory([initialShiftInfo]);
        setPageLoading(false);
        return;
      }

      try {
        setNegotiationHistory([initialShiftInfo, ...shift.rateNegotiation.history]);
        setPageLoading(false);
      } catch (error) {
        logError("Error in Rate Negotiation History Fetch", { error });
        setPageLoading(false);
      }
    };

    getHistory();
  }, [initialShiftInfo, shift]);

  useEffect(() => {
    if (!workApproved) {
      return;
    }

    const acceptedHistoryItem = {
      action: NegotiationHistoryAction.ACCEPTED,
      createdByUserId: userId,
      createdByUserRole: NegotiationRateRole.WORKER,
      payRate: currentRate,
      createdAt: moment().toString(),
      start: initialShiftInfo.start,
      end: initialShiftInfo.end,
    };
    const newHistory = [
      initialShiftInfo,
      ...(shift?.rateNegotiation?.history || []),
      acceptedHistoryItem,
    ];

    setNegotiationHistory(newHistory as RateNegotiationHistoryResponse[]);

    const rateNegotiation = {
      ...shift.rateNegotiation,
      history: newHistory,
    } as RateNegotiationSchema;

    const metadata: NegotiationMetadata = {
      ...(rateNegotiation?.metadata || {}),
      lastOffer: acceptedHistoryItem as RateNegotiationHistoryResponse,
    };

    rateNegotiation["metadata"] = metadata;
  }, [currentRate, shift.rateNegotiation, userId, workApproved, initialShiftInfo]);

  const increasePayRate = () => {
    userHasChangedRate.current = true;
    setCurrentRate((prevRate) => {
      if (prevRate + 1 > upperLimit) {
        return upperLimit;
      } else {
        return Math.floor(prevRate + 1);
      }
    });
  };

  const decreasePayRate = () => {
    userHasChangedRate.current = true;
    setCurrentRate((prevRate) => {
      if (lowerLimit > prevRate - 1) {
        return lowerLimit;
      } else {
        return Math.ceil(prevRate - 1);
      }
    });
  };

  const rateIsDifferent = currentRate !== lowerLimit;
  const proposeButtonText = rateIsDifferent
    ? `Propose ${formatDollarsAsUsd(currentRate, { minimumFractionDigits: 0 })}/hr`
    : `Book at ${formatDollarsAsUsd(lowerLimit, { minimumFractionDigits: 0 })}/hr`;

  const workerLastProposal = useMemo(
    () =>
      negotiationHistory?.length &&
      negotiationHistory[negotiationHistory.length - 1]?.createdByUserRole ===
        NegotiationRateRole.WORKER
        ? negotiationHistory[negotiationHistory.length - 1].payRate
        : 0,
    [negotiationHistory]
  );

  const invalidateShiftModalQuery = async () => {
    // Required to update negotiation status in Shift Modal
    await queryClient.invalidateQueries({ queryKey: [GET_SHIFT_MODAL_URL + shift._id] });
  };

  const handleProposal = async () => {
    try {
      setRequestLoading(true);
      let negotiation: RateNegotiationSchema;
      const negotiationId = shift?.rateNegotiation?._id;
      const newProposal = { payRate: currentRate };
      if (negotiationId) {
        const data = await proposeNewRate({
          shiftId: shift?._id ?? "",
          negotiationId,
          ...newProposal,
        });
        ({ negotiation } = data);
      } else {
        const { rateNegotiationId } = await createNegotiation({
          shiftId: shift?._id ?? "",
          offerId: shift?.offer?.id ?? "",
          ...newProposal,
          source:
            hcfProfileSource === HCF_PROFILE_SOURCE.PERMANENT_PLACEMENTS_WORK_TRIALS
              ? "PERMANENT_PLACEMENTS_WORK_TRIALS"
              : undefined,
        });

        if (hcfProfileSource === HCF_PROFILE_SOURCE.PERMANENT_PLACEMENTS_WORK_TRIALS && shift) {
          logEvent(APP_V2_APP_EVENTS.PLACEMENT_WORK_TRIAL_SHIFT_BOOKED, {
            workerId: userId,
            userEmail: workerEmail,
            workerAlreadyWorked,
            workplaceId: shift.facility?.userId,
            shiftId: shift._id,
            source: "rate_negotiation",
          });
        }

        const data = await fetchNegotiationById(rateNegotiationId);
        ({ negotiation } = data);
      }

      logEvent(USER_EVENTS.NEGOTIATION_RATE_PROPOSED, {
        shiftId: shift._id,
        negotiationId: negotiation._id,
        proposals:
          negotiationHistory.filter(
            (historyItem) =>
              historyItem.action === NegotiationHistoryAction.PROPOSED ||
              historyItem.action === NegotiationHistoryAction.STARTED
          ).length + 1,
        proposed_rate: currentRate,
        previous_rate: negotiationHistory[negotiationHistory.length - 1].payRate,
      });

      setNegotiationHistory([initialShiftInfo, ...negotiation.history]);
      const metadata = {
        ...(negotiation?.metadata || {}),
        lastOffer: negotiation.history[negotiation.history.length - 1],
      };
      negotiation["metadata"] = metadata;
      updateShiftRateNegotiation(negotiation);

      await invalidateShiftModalQuery();

      closeCommitModal();
      setRequestLoading(false);
    } catch (error) {
      setRequestLoading(false);
      logError("Error In Proposal", { error });
      showErrorToast("Error In Proposal");
    }
  };

  const handleEndNegotiation = async () => {
    const negotiationId = shift?.rateNegotiation?._id;
    if (!negotiationId) {
      throw Error("Negotiation Id is required");
    }
    setRequestLoading(true);
    try {
      await endNegotiation(negotiationId);
      const { negotiation } = await fetchNegotiationById(negotiationId);

      logEvent(USER_EVENTS.NEGOTIATION_ENDED, {
        shiftId: shift._id,
        negotiationId: negotiationId,
        proposals: negotiationHistory.filter(
          (historyItem) =>
            historyItem.action === NegotiationHistoryAction.PROPOSED ||
            historyItem.action === NegotiationHistoryAction.STARTED
        ).length,
      });

      setNegotiationHistory([
        ...(negotiationHistory || []),
        {
          action: NegotiationHistoryAction.REJECTED,
          createdByUserId: String(userId),
          createdByUserRole: NegotiationRateRole.WORKER,
          payRate: currentRate,
          createdAt: moment().toString(),
        },
      ]);

      const metadata = {
        ...(negotiation?.metadata || {}),
        lastOffer: negotiation.history[negotiation.history.length - 1],
      };
      negotiation["metadata"] = metadata;

      updateShiftRateNegotiation(negotiation);

      await invalidateShiftModalQuery();

      closeCommitModal();
      setRequestLoading(false);
    } catch (error) {
      setRequestLoading(false);
      logError("Error Ending Negotiation", { error: JSON.stringify((error as Error)?.stack) });
      showErrorToast("Error Ending Negotiation");
    }
  };

  const isWorkerAllowedToPropose =
    !workApproved &&
    (!shift?.rateNegotiation || shift?.rateNegotiation?.status === NegotiationStatus.OPEN);

  const isWorkerProposing = rateIsDifferent;

  const openRateNegotiationSupportLink = async () => {
    await openArticle(rateNegotiationWorkerSupportLink);
    return;
  };

  return (
    <FullScreenDialog
      modalState={{
        modalIsOpen: isOpen,
        modalIsClosed: !isOpen,
        openModal: () => {},
        closeModal: () => closeModal(),
        modalStatus: "open",
        setModalStatus: () => {},
      }}
    >
      <FullScreenDialogTitle
        onClose={() => {
          closeModal();
        }}
      >
        Shift Rate Negotiation
      </FullScreenDialogTitle>
      <DialogContent dividers>
        <RateNegotiationShiftDetails shift={shift} />
        {pageLoading || isGetNegotiationShiftDetailsLoading ? (
          <Stack alignItems="center">
            <CircularProgress />
          </Stack>
        ) : (
          <>
            {shift?.rateNegotiation && (
              <IconButton
                edge="end"
                color="inherit"
                onClick={async () => await openRateNegotiationSupportLink()}
                data-testid="rn-information-icon"
              >
                <InfoIcon />
              </IconButton>
            )}
            <NegotiationHistory
              history={negotiationHistory}
              rateNegotiationStatus={shift?.rateNegotiation?.status}
              workApproved={workApproved}
              openRateNegotiationSupportLink={openRateNegotiationSupportLink}
            />
            {isWorkerAllowedToPropose && (
              <RateStepper
                proposedBy={
                  shift.rateNegotiation?.metadata?.lastOffer?.createdByUserRole ||
                  NegotiationRateRole.NEW_PROPOSAL
                }
                currentRate={workerLastProposal || currentRate}
                increaseRate={increasePayRate}
                decreaseRate={decreasePayRate}
                upperLimit={upperLimit}
                lowerLimit={lowerLimit}
              />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ flexDirection: "column" }} disableSpacing>
        <NegotiationToolFooter
          shift={shift}
          negotiationHistory={negotiationHistory}
          workApproved={workApproved}
          closeModal={closeModal}
          isWorkerProposing={isWorkerProposing}
          showCommitModal={showCommitModal}
          pageLoading={pageLoading}
          isLoadingShiftBook={isLoadingShiftBook}
          proposeButtonText={proposeButtonText}
          onClickProposeButton={() => {
            showCommitModal({
              isOpen: true,
              onCommit: handleProposal,
              modalType: NegotiationCommitType.PROPOSE_NEW_RATE,
              rate: currentRate,
            });
          }}
          handleEndNegotiation={handleEndNegotiation}
          shiftBookability={shiftBookability}
          bookabilityNegotiationMetadata={{
            negotiationId: shift?.rateNegotiation?._id ?? "",
            bookRate: lowerLimit,
          }}
          hcfProfileSource={hcfProfileSource}
          onBookShift={onBookShift}
        />
      </DialogActions>
    </FullScreenDialog>
  );
};
